import type { Project } from '@/domain/types';
import { touchProject } from '@/domain/mutate';
import { CommandError, type CommandDefinition, type CommandRecord, type ErasedCommandDefinition } from './types';

const registry = new Map<string, ErasedCommandDefinition>();

/**
 * Registers a command. The single cast here is the one place where the typed
 * definition meets the erased registry; every caller above it stays typed.
 */
export function defineCommand<P>(definition: CommandDefinition<P>): CommandDefinition<P> {
  if (registry.has(definition.type)) {
    throw new Error(`[stage-zero] duplicate command registration: ${definition.type}`);
  }
  registry.set(definition.type, definition as unknown as ErasedCommandDefinition);
  return definition;
}

export function getCommandDefinition(type: string): ErasedCommandDefinition | undefined {
  return registry.get(type);
}

export function listCommandTypes(): string[] {
  return [...registry.keys()].sort();
}

export interface AppliedCommand {
  project: Project;
  label: string;
  coalesceKey: string | null;
  /** False when the command was valid but changed nothing (a no-op drag, say). */
  changed: boolean;
}

/**
 * Validates and runs a command record against a project.
 * Throws `CommandError` for unknown types or parameters that fail validation —
 * the caller decides whether that is a bug (internal call) or a rejected
 * request (scripted or generated input).
 */
export function runCommand(project: Project, record: CommandRecord): AppliedCommand {
  const definition = registry.get(record.type);
  if (!definition) throw new CommandError(record.type, `Unknown command "${record.type}".`);

  const params = definition.parse(record.params ?? {});
  if (params === null || params === undefined) {
    throw new CommandError(record.type, `Invalid parameters for command "${record.type}".`);
  }

  const next = definition.apply(project, params);
  const changed = next !== project;
  return {
    project: changed ? touchProject(next) : project,
    label: definition.label(params, project),
    coalesceKey: definition.coalesceKey?.(params) ?? null,
    changed,
  };
}

/** Convenience for internal call sites that already hold typed params. */
export function command<P>(definition: CommandDefinition<P>, params: P): CommandRecord {
  return { type: definition.type, params: params as unknown as Record<string, unknown> };
}
